import {
	GQLCartFragment,
	GQLCartItemFragment,
	GQLCartShippingMethodType,
	GQLDesignFragment,
	GQLOperationalSettingsFragment,
} from '@theprintbar/graphql';
import { couponIsValid, getCurrencyValue, getMerchTilePrice, getShippingPrice, invariant } from '@theprintbar/shared';

export enum CartType {
	Empty = 'empty',
	Sameday = 'sameday',
	Standard = 'standard',
}

const storageKey = 'cartType';
export function getCartType(): CartType {
	return (typeof window !== 'undefined' && (window.localStorage.getItem(storageKey) as CartType)) || CartType.Empty;
}

export function setCartType(cart?: GQLCartFragment | null) {
	window.localStorage.setItem(storageKey, cart?.items?.length ? (cart.sameday ? CartType.Sameday : CartType.Standard) : CartType.Empty);
}

export function getCartItemQuantity(item: GQLCartItemFragment) {
	return item.lines?.reduce((acc, line) => acc + line.quantity, 0) || 0;
}

export function getCartItemCost(settings: GQLOperationalSettingsFragment, item: GQLCartItemFragment, sameday?: boolean) {
	invariant(!!item.merch, 'No merch found');
	return getMerchTilePrice({
		merch: item.merch,
		quantity: getCartItemQuantity(item),
		settings,
		isSameday: typeof sameday === 'boolean' ? sameday : !!item.merch.preferSameday,
	});
}

export function getCartItemWeight(item: GQLCartItemFragment) {
	return item.lines?.reduce((acc, line) => acc + (line.variant?.weight || 0) * line.quantity, 0) || 0;
}

export function getCartQuantity(cart?: GQLCartFragment | null) {
	return !!cart ? cart.items?.reduce((acc, item) => acc + getCartItemQuantity(item), 0) || 0 : 0;
}

export function getCartWeight(cart?: Pick<GQLCartFragment, 'items'> | null) {
	return !!cart ? cart.items?.reduce((acc, item) => acc + getCartItemWeight(item), 0) || 0 : 0;
}

export function getCartDesigns(cart?: GQLCartFragment | null) {
	return (
		cart?.items?.reduce((acc, item) => {
			item.merch.decorations?.forEach(decoration => {
				if (!!decoration.design && !acc.find(d => d.id === decoration.design?.id)) {
					acc.push(decoration.design);
				}
			});
			return acc;
		}, [] as GQLDesignFragment[]) || []
	);
}

export function getCartShippingCost(settings: GQLOperationalSettingsFragment, cart: GQLCartFragment) {
	return getShippingPrice({ settings, shippingMethod: cart.shippingMethod, weight: getCartWeight(cart) });
}

export function getCartSubtotalCost(settings: GQLOperationalSettingsFragment, cart: GQLCartFragment) {
	let subtotal = cart.items?.reduce((acc, item) => acc + getCartItemCost(settings, item).total, 0) || 0;
	return getCurrencyValue(subtotal);
}

interface CartCouponDiscount {
	discountOn: string | null | undefined;
	code: string | null | undefined;
	discount: number;
	isValid: boolean;
	description?: string;
}
export function getCartCouponDiscount(settings: GQLOperationalSettingsFragment, cart: GQLCartFragment): CartCouponDiscount {
	if (!cart.coupon) return { discountOn: null, code: null, discount: 0, isValid: false };
	if (!couponIsValid(cart.coupon)) return { discountOn: null, code: cart.coupon.code, discount: 0, isValid: false };

	const items = getCartSubtotalCost(settings, cart);
	const shipping = getCartShippingCost(settings, cart);
	const discountAmount = cart.coupon.discountOn === 'items' ? getCartSubtotalCost(settings, cart) : items + shipping;
	const fullDiscount = discountAmount * (cart.coupon.discount / 100);
	const withLimit =
		cart.coupon.hasLimit && cart.coupon.discountRemaining
			? Math.min(parseFloat(cart.coupon.discountRemaining), fullDiscount)
			: fullDiscount;

	return {
		discountOn: cart.coupon.discountOn,
		code: cart.coupon.code,
		description: cart.coupon.shortDescription,
		discount: getCurrencyValue(withLimit),
		isValid: true,
	};
}

export function getCartPrice(settings: GQLOperationalSettingsFragment, cart: GQLCartFragment, includeDiscount: boolean = true) {
	const shipping = getCartShippingCost(settings, cart);
	const items = getCartSubtotalCost(settings, cart);
	const couponDiscount = includeDiscount ? getCartCouponDiscount(settings, cart) : undefined;
	const total = getCurrencyValue(shipping + items - (couponDiscount?.discount || 0));
	const gst = cart.shippingMethod === GQLCartShippingMethodType.International ? 0 : getCurrencyValue(total / 11);
	return {
		total,
		shipping,
		items,
		couponDiscount,
		gst,
	};
}

export function getCartItemColour(cartItem: GQLCartItemFragment) {
	return cartItem.merch.product?.colours?.find(({ id }) => id === cartItem.lines?.[0]?.variant?.colour.id)?.colour;
}
